/**
 * ImageTile - Unified image preview component
 *
 * Consolidates all image preview functionality into one reusable component.
 * Used for: gebreken afbeeldingen, chat previews, files page thumbnails, upload progress.
 */

import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { X, Plus, Eye, Loader2, MessageSquareText, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPhotoDate } from "@/lib/extract-photo-date";

// =============================================================================
// TYPES
// =============================================================================

export interface ImageTileProps {
  /** Image URL or base64 data URL */
  src?: string;
  /** Local file for instant preview (creates object URL) */
  file?: File;
  /** Alt text for the image */
  alt?: string;

  /** Size variant: sm=64px, md=96px, lg=128px */
  size?: "sm" | "md" | "lg";

  /** Current status of the image */
  status?: "idle" | "processing" | "uploading" | "error";
  /** Upload progress (0-100), only used when status is "uploading" */
  progress?: number;
  /** Processing stage (only used when status is "processing") */
  processingStage?: "optimizing" | "validating" | "encoding" | "thumbnailing";
  /** Disable all interactions */
  disabled?: boolean;

  /** Show at 50% opacity (for unselected images) */
  inactive?: boolean;
  /** Apply rounded corners */
  rounded?: boolean;

  /** Show date overlay at bottom */
  showDate?: boolean;
  /** Date to display (required if showDate is true) */
  photoDate?: Date;
  /** Badge indicator in bottom-left corner */
  badge?: "audio" | "description";

  /** Show + overlay button (for activating inactive images) */
  onActivate?: () => void;
  /** Show X button in top-right (for removing) */
  onRemove?: () => void;
  /** Show eye button (for preview, used in FilesPage) */
  onPreview?: () => void;
  /** Generic click handler on the image */
  onClick?: () => void;

  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// SIZE CLASSES
// =============================================================================

function getSizeClasses(size: "sm" | "md" | "lg"): string {
  switch (size) {
    case "sm":
      return "h-16 w-16";
    case "lg":
      return "h-32 w-32";
    default:
      return "h-24 w-24";
  }
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface LoadingOverlayProps {
  status: "processing" | "uploading";
  progress?: number;
  processingStage?: "optimizing" | "validating" | "encoding" | "thumbnailing";
}

function LoadingOverlay({ status, progress, processingStage }: LoadingOverlayProps) {
  // Stage-specific text
  const stageText = processingStage === "optimizing" ? "Optimaliseren..."
    : processingStage === "validating" ? "Valideren..."
    : processingStage === "encoding" ? "Encoderen..."
    : processingStage === "thumbnailing" ? "Thumbnail maken..."
    : "Verwerken...";

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
      <Loader2 className="h-6 w-6 animate-spin text-white" />
      <span className="mt-1 text-[10px] text-white">
        {status === "uploading" ? `${Math.round(progress ?? 0)}%` : stageText}
      </span>
    </div>
  );
}

interface DateOverlayProps {
  date: Date;
}

function DateOverlay({ date }: DateOverlayProps) {
  return (
    <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
      <span className="block truncate text-[10px] text-white">
        {formatPhotoDate(date)}
      </span>
    </div>
  );
}

interface BadgeIndicatorProps {
  type: "audio" | "description";
}

function BadgeIndicator({ type }: BadgeIndicatorProps) {
  const Icon = type === "audio" ? Mic : MessageSquareText;

  return (
    <div
      className="absolute bottom-1 left-1 z-10 rounded-full bg-primary p-0.5 text-primary-foreground"
      title={type === "audio" ? "Heeft audio" : "Heeft beschrijving"}
    >
      <Icon className="h-3 w-3" />
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ImageTile({
  src,
  file,
  alt,
  size = "md",
  status = "idle",
  progress,
  processingStage,
  disabled = false,
  inactive = false,
  rounded = false,
  showDate = false,
  photoDate,
  badge,
  onActivate,
  onRemove,
  onPreview,
  onClick,
  className,
}: ImageTileProps) {
  const { t } = useTranslation("common");
  const [imageError, setImageError] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  // Create object URL for File input
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [file]);

  // Determine the image source
  const imageSrc = objectUrl || src;

  // Determine if we're in a loading state
  const isLoading = status === "processing" || status === "uploading";

  // Determine if interactions are disabled
  const isDisabled = disabled || isLoading;

  // Show activate overlay for inactive images (only if onActivate is provided)
  const showActivateOverlay = inactive && onActivate && !isDisabled;

  // Size classes
  const sizeClasses = getSizeClasses(size);

  return (
    <div
      className={cn(
        "group relative overflow-hidden bg-muted",
        sizeClasses,
        inactive && !isLoading && "opacity-50",
        rounded && "rounded-md",
        onClick && !isDisabled && "cursor-pointer",
        className
      )}
      onClick={!isDisabled ? onClick : undefined}
    >
      {/* Image */}
      {imageSrc && !imageError ? (
        <img
          src={imageSrc}
          alt={alt ?? t("common.image")}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted">
          <span className="text-xs text-muted-foreground">
            {imageError ? "Fout" : "..."}
          </span>
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && <LoadingOverlay status={status} progress={progress} processingStage={processingStage} />}

      {/* Activate overlay (+ button for inactive images) */}
      {showActivateOverlay && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onActivate?.();
          }}
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40"
        >
          <Plus className="h-8 w-8 text-white" />
        </button>
      )}

      {/* Remove button (X) */}
      {onRemove && !isDisabled && !inactive && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute right-1 top-1 bg-utrecht-red p-1 text-white"
          aria-label="Verwijderen"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Preview button (eye) - for FilesPage style */}
      {onPreview && !isDisabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          className="absolute right-1 top-1 rounded-md bg-secondary p-1.5 text-muted-foreground transition-colors hover:bg-utrecht-blue hover:text-white"
          aria-label="Bekijken"
        >
          <Eye className="h-4 w-4" />
        </button>
      )}

      {/* Badge indicator */}
      {badge && !isLoading && <BadgeIndicator type={badge} />}

      {/* Date overlay */}
      {showDate && photoDate && !isLoading && <DateOverlay date={photoDate} />}
    </div>
  );
}
